import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { trigger, transition, style, animate } from '@angular/animations';
import { VoucherService } from '../../services/voucher.service';
import { VoucherDto, VoucherStatus, getVoucherStatusLabel, isVoucherClaimable, ClaimVouchersDto } from '../../models/voucher.model';
import { OtpVerificationComponent } from '../shared/otp-verification/otp-verification.component';

@Component({
    selector: 'app-customer-vouchers',
    standalone: true,
    imports: [CommonModule, FormsModule, OtpVerificationComponent],
    templateUrl: './customer-vouchers.component.html',
    styleUrl: './customer-vouchers.component.css',
    animations: [
        trigger('slideDown', [
            transition(':enter', [
                style({ height: 0, opacity: 0, overflow: 'hidden' }),
                animate('300ms ease-out', style({ height: '*', opacity: 1 }))
            ]),
            transition(':leave', [
                animate('200ms ease-in', style({ height: 0, opacity: 0, overflow: 'hidden' }))
            ])
        ])
    ]
})
export class CustomerVouchersComponent implements OnInit {
    @ViewChild(OtpVerificationComponent) otpComponent!: OtpVerificationComponent;

    customerId: string = '';
    customerName: string = '';
    vouchers: VoucherDto[] = [];
    isLoading: boolean = false;
    errorMessage: string = '';



    // Bulk Claim State
    billAmount: number | null = null;
    selectedVoucherIds: Set<string> = new Set();
    isBulkClaiming: boolean = false;
    showBulkOtpSection: boolean = false;

    // Simplified Bulk Claim State (OTP handled by component)
    bulkClaimData = {
        redemptionReference: '',
        notes: '',
        isProcessing: false,
        errorMessage: '',
        successMessage: '',
        otpVerified: false
    };

    // Expose enum to template
    VoucherStatus = VoucherStatus;

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private voucherService: VoucherService
    ) { }

    ngOnInit(): void {
        // Get customer ID from route params
        this.customerId = this.route.snapshot.paramMap.get('customerId') || '';

        // Get customer name from navigation state
        const navigation = this.router.getCurrentNavigation();
        if (navigation?.extras?.state) {
            this.customerName = navigation.extras.state['customerName'] || '';
        }

        // If no customer name in state, try to get it from history state
        if (!this.customerName && history.state) {
            this.customerName = history.state['customerName'] || 'Customer';
        }

        // Validate customer ID
        if (!this.customerId) {
            this.errorMessage = 'Customer ID is required';
            return;
        }

        // Load vouchers for this customer
        this.loadVouchers();
    }

    /**
     * Load vouchers from API
     */
    loadVouchers(): void {
        this.isLoading = true;
        this.errorMessage = '';

        this.voucherService.getCustomerVouchers(this.customerId).subscribe({
            next: (vouchers) => {
                this.vouchers = vouchers;
                this.isLoading = false;

                // Update customer name if available from voucher data
                if (vouchers.length > 0 && !this.customerName) {
                    this.customerName = vouchers[0].customerName;
                }
            },
            error: (error) => {
                this.isLoading = false;
                this.errorMessage = error.error?.message || 'Failed to load vouchers. Please try again.';
                console.error('Error loading vouchers:', error);
            }
        });
    }

    /**
     * Navigate back to customer search
     */
    goBack(): void {
        this.router.navigate(['/']);
    }





    /**
     * Get status label for display
     */
    /**
     * Handle bill amount change
     * (No longer auto-deselects vouchers - validation happens during claim)
     */
    onBillAmountChange(): void {
        // Bill amount changed - validation will happen during claim
        // No need to deselect vouchers here
    }

    /**
     * Toggle voucher selection
     */
    toggleVoucherSelection(voucher: VoucherDto, event?: Event): void {
        if (event) {
            event.stopPropagation();
        }

        if (!this.canSelectVoucher(voucher)) {
            return;
        }

        if (this.selectedVoucherIds.has(voucher.id!)) {
            this.selectedVoucherIds.delete(voucher.id!);
        } else {
            this.selectedVoucherIds.add(voucher.id!);
        }
    }

    /**
     * Check if voucher is selected
     */
    isVoucherSelected(voucherId: string): boolean {
        return this.selectedVoucherIds.has(voucherId);
    }

    /**
     * Check if a voucher can be selected
     * (Bill amount validation is done during claim, not during selection)
     */
    canSelectVoucher(voucher: VoucherDto): boolean {
        return this.isClaimable(voucher);
    }

    /**
     * Get total value of selected vouchers
     */
    getUserSelectedVouchers(): VoucherDto[] {
        return this.vouchers.filter(v => this.selectedVoucherIds.has(v.id!));
    }

    getTotalSelectedValue(): number {
        return this.getUserSelectedVouchers().reduce((sum, v) => sum + (v.voucherAmount || 0), 0);
    }

    /**
     * Get total minimum purchase required for selected vouchers
     */
    getTotalMinPurchaseRequired(): number {
        return this.getUserSelectedVouchers().reduce((sum, v) => sum + (v.minimumPurchaseAmount || 0), 0);
    }

    /**
     * Get balance to pay after applying vouchers
     * Returns the remaining amount customer needs to pay after voucher discount
     */
    getBalanceToPay(): number {
        if (this.billAmount === null || this.billAmount === undefined) {
            return 0;
        }
        const balance = this.billAmount - this.getTotalSelectedValue();
        return balance > 0 ? balance : 0;
    }

    /**
     * Check if bulk claim is valid
     * "total selected miniumPurchaseAmount should be lesser than bill amount"
     */
    isBulkClaimEligible(): boolean {
        if (this.selectedVoucherIds.size === 0) return false;
        if (this.billAmount === null) return false;

        const totalMinRequired = this.getTotalMinPurchaseRequired();
        return totalMinRequired <= this.billAmount;
    }

    /**
     * Initiate Bulk Claim
     */
    initiateBulkClaim(): void {
        if (!this.isBulkClaimEligible()) return;

        // Use the first selected voucher to get phone number (all belong to same customer)
        const selectedVouchers = this.getUserSelectedVouchers();
        if (selectedVouchers.length === 0) return;

        const customerPhone = selectedVouchers[0].customerPhoneNumber;
        if (!customerPhone) {
            this.errorMessage = 'Customer phone number not found';
            return;
        }

        this.showBulkOtpSection = true;
        this.bulkClaimData.otpVerified = false;
        this.bulkClaimData.errorMessage = '';
        this.bulkClaimData.successMessage = '';
    }

    closeBulkOtpSection(): void {
        this.showBulkOtpSection = false;
        this.bulkClaimData.redemptionReference = '';
        this.bulkClaimData.notes = '';
        this.bulkClaimData.otpVerified = false;
        this.bulkClaimData.errorMessage = '';
        this.bulkClaimData.successMessage = '';
        if (this.otpComponent) {
            this.otpComponent.reset();
        }
    }

    /**
     * Handle OTP verification success
     */
    onOtpVerified(): void {
        this.bulkClaimData.otpVerified = true;
        this.bulkClaimData.errorMessage = '';
        this.bulkClaimData.successMessage = 'OTP Verified! Please enter transaction details below.';

        // Don't auto-proceed - wait for user to fill redemption reference and click claim
    }

    /**
     * Handle OTP verification error
     */
    onOtpError(error: string): void {
        this.bulkClaimData.errorMessage = error;
    }

    onBulkClaimSuccess(): void {
        const selectedVouchers = this.getUserSelectedVouchers();
        if (selectedVouchers.length === 0) {
            this.bulkClaimData.errorMessage = 'No vouchers selected';
            return;
        }

        // Validate redemption reference is provided
        if (!this.isRedemptionReferenceValid()) {
            this.bulkClaimData.errorMessage = 'Transaction Reference is required';
            return;
        }

        // Server will handle all partner/user information from authentication context
        const claimRequest: ClaimVouchersDto = {
            voucherIds: Array.from(this.selectedVoucherIds),
            redemptionReference: this.bulkClaimData.redemptionReference.trim(),
            notes: this.bulkClaimData.notes?.trim() || undefined,
            billAmount: this.billAmount || undefined
        };

        this.bulkClaimData.isProcessing = true;
        this.voucherService.claimVouchers(claimRequest).subscribe({
            next: (claimedVouchers) => {
                console.log('Bulk claim successful:', claimedVouchers);
                this.bulkClaimData.isProcessing = false;
                this.bulkClaimData.successMessage = `Successfully claimed ${claimedVouchers.length} voucher(s)`;

                setTimeout(() => {
                    this.closeBulkOtpSection();
                    this.selectedVoucherIds.clear();
                    this.billAmount = null; // Clear bill amount after successful claim
                    this.loadVouchers();

                    // Scroll to top to show refreshed voucher list
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                }, 1500);
            },
            error: (error) => {
                this.bulkClaimData.isProcessing = false;
                this.bulkClaimData.errorMessage = error.error?.message || 'Failed to claim vouchers. Please try again.';
                console.error('Bulk claim error:', error);
            }
        });
    }

    /**
     * Validate that redemption reference is provided
     */
    isRedemptionReferenceValid(): boolean {
        return !!this.bulkClaimData.redemptionReference && this.bulkClaimData.redemptionReference.trim().length > 0;
    }

    /**
     * Get status label for display
     */
    getStatusLabel(status?: VoucherStatus): string {
        return getVoucherStatusLabel(status);
    }

    /**
     * Check if voucher is claimable
     */
    isClaimable(voucher: VoucherDto): boolean {
        return isVoucherClaimable(voucher);
    }

    /**
     * Format date for display
     */
    formatDate(dateString?: string): string {
        if (!dateString) return 'No expiry';

        const date = new Date(dateString);
        const now = new Date();

        // Check if expired
        if (date < now) {
            return 'Expired';
        }

        // Format date
        return date.toLocaleDateString('en-IN', {
            day: 'numeric',
            month: 'short',
            year: 'numeric'
        });
    }

    /**
     * Get status CSS class
     */
    getStatusClass(voucher: VoucherDto): string {
        if (voucher.status === VoucherStatus.Redeemed) {
            return 'redeemed';
        }
        if (voucher.status === VoucherStatus.Expired ||
            voucher.status === VoucherStatus.Cancelled ||
            voucher.status === VoucherStatus.Revoked) {
            return 'expired';
        }
        if (this.isClaimable(voucher)) {
            return 'active';
        }
        return 'expired';
    }
}
